import { useMemo, useState } from "react";
import type {
  SubtaskId,
  Task,
  TaskId,
  TaskStatus,
} from "../../../shared/types";
import { TaskListFilters } from "./TaskListFilters";
import { TaskListContentDnd } from "./TaskListContentDnd";

interface TaskListProps {
  readonly tasks: Task[];
  readonly showCategory?: boolean;
  readonly onCycleStatus: (id: TaskId) => void;
  readonly onUpdate: (
    id: TaskId,
    updates: Partial<
      Pick<Task, "title" | "priority" | "notes" | "tags" | "dueDate">
    >,
  ) => void;
  readonly onDelete: (id: TaskId) => void;
  readonly onAddSubtask: (taskId: TaskId, title: string) => void;
  readonly onToggleSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onDeleteSubtask: (taskId: TaskId, subtaskId: SubtaskId) => void;
  readonly onReorder?: (taskId: TaskId, newIndex: number) => void;
}

type FilterValue = "all" | TaskStatus;

export function TaskList({
  tasks,
  showCategory = false,
  onCycleStatus,
  onUpdate,
  onDelete,
  onAddSubtask,
  onToggleSubtask,
  onDeleteSubtask,
  onReorder,
}: TaskListProps) {
  const [filter, setFilter] = useState<FilterValue>("all");
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<TaskId>>(new Set());

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    for (const t of tasks) {
      for (const tag of t.tags) tags.add(tag);
    }
    return Array.from(tags).sort();
  }, [tasks]);

  const filtered = useMemo(() => {
    let result =
      filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
    if (selectedTag)
      result = result.filter((t) => t.tags.includes(selectedTag));
    return result;
  }, [tasks, filter, selectedTag]);

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: tasks.length };
    for (const t of tasks) result[t.status] = (result[t.status] ?? 0) + 1;
    return result;
  }, [tasks]);

  function handleSelectTask(id: TaskId) {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  }

  // Handler de réordonnancement - transmet directement au parent
  function handleReorder(taskId: TaskId, newIndex: number) {
    onReorder?.(taskId, newIndex);
  }

  return (
    <div className="flex flex-col gap-3">
      <TaskListFilters
        filter={filter}
        onFilterChange={setFilter}
        selectedTag={selectedTag}
        onTagChange={setSelectedTag}
        allTags={allTags}
        counts={counts}
      />

      <TaskListContentDnd
        tasks={filtered}
        showCategory={showCategory}
        selectedIds={selectedIds}
        onCycleStatus={onCycleStatus}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onAddSubtask={onAddSubtask}
        onToggleSubtask={onToggleSubtask}
        onDeleteSubtask={onDeleteSubtask}
        onSelect={handleSelectTask}
        onReorder={onReorder ? handleReorder : undefined}
      />
    </div>
  );
}
